import { create } from 'zustand';
import { Menu, fetchMenusByRestaurant, fetchMenuItems, fetchMenuItemNames } from '../services/menus/menusService';
import { ALL_RESTAURANTS_ID } from './appUserDefaultStore';

interface MenusStore {
  menus: Menu[];
  isLoading: boolean;
  error: string | null;
  currentRestaurantId: string | null;
  // The menu currently open in the menus page detail view. Lives in the store
  // (not in the page's local state) so it survives navigation away and back.
  // Cleared when the restaurant changes so we don't show a stale menu from
  // another restaurant.
  selectedMenuId: string | null;
  setSelectedMenuId: (id: string | null) => void;
  menuItems: Record<string, string[]>; // menuId → costPriceId[]
  menuItemNames: Record<string, string[]>; // menuId → menuItemName[]
  _loadingMenuItems: Set<string>; // guard against concurrent loadMenuItems calls
  _loadingMenuItemNames: Set<string>; // guard against concurrent loadMenuItemNames calls
  load: (restaurantId: string) => Promise<void>;
  reload: () => Promise<void>;
  clear: () => void;
  byId: (id: string) => Menu | undefined;
  isDataLoadedFor: (restaurantId: string) => boolean;
  setMenuItems: (menuId: string, costPriceIds: string[]) => void;
  getMenuItems: (menuId: string) => string[];
  loadMenuItems: (menuId: string) => Promise<string[]>;
  getMenuItemNames: (menuId: string) => string[];
  loadMenuItemNames: (menuId: string) => Promise<string[]>;
}

export const useMenusStore = create<MenusStore>((set, get) => ({
  menus: [],
  isLoading: false,
  error: null,
  currentRestaurantId: null,
  selectedMenuId: null,
  setSelectedMenuId: (id) => set({ selectedMenuId: id }),
  menuItems: {},
  menuItemNames: {},
  _loadingMenuItems: new Set<string>(),
  _loadingMenuItemNames: new Set<string>(),

  load: async (restaurantId: string) => {
    const currentState = get();
    if (currentState.isLoading) return;

    // Clear the open-menu selection when switching restaurants so we don't
    // leave a stale menu (from another restaurant) marked as selected.
    const restaurantChanged = currentState.currentRestaurantId !== restaurantId;

    if (restaurantId === ALL_RESTAURANTS_ID) {
      set({
        menus: [],
        currentRestaurantId: restaurantId,
        error: null,
        ...(restaurantChanged ? { selectedMenuId: null } : {}),
      });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const menus = await fetchMenusByRestaurant(restaurantId);
      // Reset per-menu items so a restaurant switch does not leave stale entries.
      set({
        menus,
        currentRestaurantId: restaurantId,
        menuItems: {},
        error: null,
        ...(restaurantChanged ? { selectedMenuId: null } : {}),
      });
      // Eagerly fetch items for every menu so menu-filter consumers (charts page,
      // cost price page) never render with an empty map on the first click.
      await Promise.all(menus.map((m) => get().loadMenuItems(m.id)));
      set({ isLoading: false });
    } catch (error) {
      console.error('Failed to load menus:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load menus',
      });
    }
  },

  reload: async () => {
    const state = get();
    if (state.currentRestaurantId && state.currentRestaurantId !== ALL_RESTAURANTS_ID) {
      set({ isLoading: false }); // reset guard so load() can run
      await get().load(state.currentRestaurantId);
    }
  },

  clear: () => {
    set({ menus: [], currentRestaurantId: null, selectedMenuId: null, isLoading: false, error: null, menuItems: {}, menuItemNames: {} });
  },

  byId: (id: string) => {
    return get().menus.find((m) => m.id === id);
  },

  isDataLoadedFor: (restaurantId: string) => {
    return get().currentRestaurantId === restaurantId;
  },

  setMenuItems: (menuId: string, costPriceIds: string[]) => {
    set((state) => ({
      menuItems: { ...state.menuItems, [menuId]: costPriceIds },
    }));
  },

  getMenuItems: (menuId: string) => {
    return get().menuItems[menuId] || [];
  },

  loadMenuItems: async (menuId: string) => {
    const loadingSet = get()._loadingMenuItems;
    if (loadingSet.has(menuId)) return get().getMenuItems(menuId);
    loadingSet.add(menuId);
    try {
      const ids = await fetchMenuItems(menuId);
      get().setMenuItems(menuId, ids);
      return ids;
    } catch (error) {
      console.error('Failed to load menu items:', error);
      return [];
    } finally {
      loadingSet.delete(menuId);
    }
  },

  getMenuItemNames: (menuId: string) => {
    return get().menuItemNames[menuId] || [];
  },

  loadMenuItemNames: async (menuId: string) => {
    const loadingSet = get()._loadingMenuItemNames;
    if (loadingSet.has(menuId)) return get().getMenuItemNames(menuId);
    loadingSet.add(menuId);
    try {
      const names = await fetchMenuItemNames(menuId);
      set((state) => ({
        menuItemNames: { ...state.menuItemNames, [menuId]: names },
      }));
      return names;
    } catch (error) {
      console.error('Failed to load menu item names:', error);
      return [];
    } finally {
      loadingSet.delete(menuId);
    }
  },
}));
